import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ACTIVITY_CATEGORIES,
  ACTIVITY_CATEGORY_VALUES,
  ACTIVITY_TABLE_ID,
  isCategoryVisibleTo,
  ROLES,
  STALLED_THRESHOLD_KEY,
  type ActivityCategory,
  type ActivityItem,
} from "@salesboard/contracts";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { useOpenDetail } from "@/app/useOpenDetail";
import { ListToolbar } from "@/components/ui/data-table/ListToolbar";
import { PaginationControl } from "@/components/ui/data-table/PaginationControl";
import { EmptyState, ErrorState, LoadingState, NoResultState } from "@/components/ui/states";
import { ActivityBlocks } from "./ActivityBlocks";
import { useSession } from "@/features/auth/useSession";
import { useOwners } from "@/features/opportunities/hooks";
import { useServerTable, type FilterDefs } from "@/hooks/useServerTable";
import { useT, type MessageKey } from "@/i18n";
import { ApiClientError } from "@/lib/api";
import { useActivity } from "./hooks";

/** The three scope groups, named for a reader. Typed against the catalogue, so a renamed key fails `tsc -b`. */
const CATEGORY_LABEL_KEYS = {
  [ACTIVITY_CATEGORIES.opportunities]: "activity.categoryOpportunities",
  [ACTIVITY_CATEGORIES.administration]: "activity.categoryAdministration",
  [ACTIVITY_CATEGORIES.configuration]: "activity.categoryConfiguration",
} as const satisfies Record<ActivityCategory, MessageKey>;

/**
 * Settings' key–value keys are machine strings; the ones the product knows get a name.
 *
 * Partial on purpose — `Record<string, …>` rather than a total map over the setting keys,
 * because a setting added by migration must still render as *something* rather than failing to
 * compile a screen it has nothing to do with. An unknown key falls back to itself.
 */
const SETTING_LABEL_KEYS: Partial<Record<string, MessageKey>> = {
  [STALLED_THRESHOLD_KEY]: "settings.thresholdTitle",
};

/**
 * Activity — one place to answer "what has happened lately, across everything".
 *
 * The whole company's audit events as a single reverse-chronological timeline, rendered by
 * the **same** `AuditTimeline` and the same presentation registry every record's own history
 * uses. That is the design's load-bearing choice: with one presentation for one event store,
 * the feed cannot tell a different story than the record it points at.
 *
 * Three things this screen deliberately does not have:
 *
 * - **No creating action.** A feed of what happened creates nothing, so the control row
 *   actions row is empty rather than carrying something invented to fill it.
 * - **No date-range filter.** The two questions the feed answers are "what happened" and
 *   "what has X been doing"; pagination reaches the past. A range is a third question nobody
 *   has asked yet.
 * - **No sort control.** A log has one meaningful order and this is it.
 */
export function ActivityPage(): React.JSX.Element {
  const t = useT();
  const session = useSession();
  const navigate = useNavigate();
  const openDetail = useOpenDetail();
  const role = session.data?.role;

  useDocumentTitle(t("activity.title"));

  const owners = useOwners();
  const ownerOptions = owners.data;

  const filters: FilterDefs = useMemo(
    () => ({
      // Full-text over actor, record and recorded values — folded server-side, so "skoda"
      // finds Škoda. First in the set because it is the question the feed is asked most.
      q: { type: "search" },
      actor: {
        type: "select",
        label: t("filter.actor"),
        options: (ownerOptions ?? []).map((owner) => ({ value: owner.id, label: owner.name })),
      },
      category: {
        type: "select",
        label: t("filter.category"),
        /**
         * **Administration is absent for a Sales Manager**, not offered-and-empty.
         *
         * Where a capability is absent its entry point is absent — the book's navigation rule,
         * applied to a filter option. The server does not depend on this: `listActivity`
         * intersects the role's scopes with the category's, so a manager who types the
         * parameter into the URL anyway gets an empty page and the no-result state naming the
         * filter that excluded everything. That is the honest answer; *ignoring* the parameter
         * would leave a chip claiming a filter that was never applied, which is the one thing
         * the filter contract forbids.
         */
        options: ACTIVITY_CATEGORY_VALUES.filter(
          (category) => !role || isCategoryVisibleTo(role, category),
        ).map((category) => ({ value: category, label: t(CATEGORY_LABEL_KEYS[category]) })),
      },
    }),
    [t, ownerOptions, role],
  );

  const table = useServerTable(ACTIVITY_TABLE_ID, filters, { field: "occurredAt", direction: "desc" });
  const query = useActivity(table.queryParams);

  return (
    <>
      <h1 className="sr-only">{t("activity.title")}</h1>

      <ListToolbar table={table} defs={filters} searchPlaceholder={t("activity.searchPlaceholder")} />

      {query.isPending && <LoadingState rows={8} label={t("activity.loading")} />}

      {query.error && (
        <ErrorState
          message={query.error instanceof ApiClientError ? query.error.message : t("activity.loadFailed")}
          requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && query.data.items.length === 0 && table.activeFilterCount === 0 && (
        <EmptyState title={t("activity.emptyTitle")} description={t("activity.emptyDescription")} />
      )}

      {query.data && query.data.items.length === 0 && table.activeFilterCount > 0 && (
        <NoResultState activeFilters={table.activeFilterLabels} onClear={table.clearFilters} />
      )}

      {query.data && query.data.items.length > 0 && (
        <>
          <ActivityBlocks
            items={query.data.items}
            renderRecord={(item) => (
              <RecordLink
                item={item}
                onOpenDetail={openDetail}
                onOpenSettings={() => void navigate("/admin/settings")}
                canReachSettings={role === ROLES.headOfSales}
              />
            )}
          />
          <PaginationControl
            page={query.data.page}
            perPage={query.data.perPage}
            total={query.data.total}
            onPageChange={table.setPage}
            onPerPageChange={table.setPerPage}
          />
        </>
      )}
    </>
  );
}

/**
 * The record a row happened to, as something you can open.
 *
 * No dead-end rows — the analytics click-through rule applied to history. Opportunities and
 * people have their own modal at their own slug; stages and settings do not, so they lead to
 * the Settings screen that holds them.
 *
 * **A configuration row is plain text for a Sales Manager.** `/admin/settings` is Head-of-Sales
 * only and would bounce them straight back to the board, and a link that refuses is worse than
 * no link — the same reason a manager sees no Administration item in the nav bar. Managers still
 * *read* these events: a stage rename is why their board looks different.
 */
function RecordLink({
  item,
  onOpenDetail,
  onOpenSettings,
  canReachSettings,
}: {
  item: ActivityItem;
  onOpenDetail: (to: string) => void;
  onOpenSettings: () => void;
  canReachSettings: boolean;
}): React.JSX.Element | null {
  const t = useT();
  const { recordLabel, recordSlug, scope } = item;
  if (!recordLabel) return null;

  const settingKey = scope === "setting" ? SETTING_LABEL_KEYS[recordLabel] : undefined;
  const label = settingKey ? t(settingKey) : recordLabel;

  const detailPath =
    recordSlug && scope === "opportunity"
      ? `/opportunity/${recordSlug}`
      : recordSlug && scope === "user"
        ? `/user/${recordSlug}`
        : undefined;

  // A deleted record is not openable: its detail answers 404, and a link that opens a
  // not-found dialog is worse than plain text — the row already says the record is gone.
  const target = detailPath && !item.recordDeleted
    ? () => onOpenDetail(detailPath)
    : (scope === "stage" || scope === "setting") && canReachSettings
      ? onOpenSettings
      : undefined;

  /**
   * An opportunity is named with its client, the way the board names it.
   *
   * "Data warehouse assessment" alone is two clients' worth of ambiguity in a feed that jumps
   * between records; the board never shows one without the other either.
   */
  const named =
    item.recordClientName !== null ? `${label} · ${item.recordClientName}` : label;

  const name = target ? (
    <button type="button" onClick={target} className="text-primary font-medium hover:underline">
      {named}
    </button>
  ) : (
    <span className="font-medium">{named}</span>
  );

  // A deleted opportunity's events stay in the feed — the feed is history, and hiding a
  // deletion's trail from a feed of record-changes would be the opposite of its job. The row
  // says so in words rather than by a colour, and (since 2026-08-25) does not pretend to be
  // openable: `target` above is withheld for a deleted record.
  return item.recordDeleted ? (
    <>
      {name} <span className="text-muted-foreground text-xs">{t("activity.deletedRecord")}</span>
    </>
  ) : (
    name
  );
}
